import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    Edit,
    SimpleForm,
    TextInput,
    Create,
    EditButton,
    NumberInput,
    CreateButton,
    ExportButton,
    TopToolbar,
    Toolbar,
    SaveButton,
    regex,
    useListContext,
    required,
    number,
    maxLength,
    BooleanInput,
    BooleanField,
    FormDataConsumer
} from 'react-admin';
import {Grid} from "@mui/material";
import {Typography} from "@material-ui/core";


const validateContestName = required();
const validateDomainName = [required(), regex(/^((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,63}$/, 'Invalid domain name structure')];
const validateGrantorApprovalTemplateId = [required(), number()];
const validateGranteeApprovalTemplateId = [required(), number()];
const validateGrantorThresholdCrossTemplateId = [number()];
const validateGranteeThresholdCrossTemplateId = [number()];
const validateEmailColor = [required(), maxLength(7), regex(/^#(([0-9a-fA-F]{2}){3})$/, 'Value provided does not match the hex color pattern, i.e.: #123456')];
const validateAlternativeText = [maxLength(280), regex(/^(?!\s)(?![^<>\n]*[<>])[\s\S]*$/, "Text cannot begin with white space and cannot contain '>' or '<' symbols")];

const ListActions = () => {
    const {ids, total} = useListContext();
    //Remove Create button if an item has already been created
    return (
        <TopToolbar>
            {total === 0 && <CreateButton/>}
            <ExportButton/>
            {/* Add other actions as needed */}
        </TopToolbar>
    );
};
const EmailBulkActionButtons = props => (
    <React.Fragment>
        {/* All buttons on list view selection have been removed */}
    </React.Fragment>
);

export const EmailList = props => {
    return (
        <List actions={<ListActions/>} bulkActionButtons={<EmailBulkActionButtons/>} {...props}>
            <Datagrid>
                <TextField label="Name des Wettbewerbs" source="contestName"/>
                <TextField label="Domain-Name" source="domainName"/>
                <TextField label="E-Mail-Genehmigungsvorlagen ID" source="grantorApprovalTemplateId"/>
                <TextField label="E-Mail-Teilnehmervorlage ID" source="granteeApprovalTemplateId"/>
                <BooleanField label="Schwellenwert-Benachrichtigungen" source="sendThresholdCrossEmailNotification" defaultValue={false}/>
                <TextField label="Optional E-Mail Alternativtext" source="alternativeDateToAction"/>
                <EditButton/>
            </Datagrid>
        </List>
    )
};

const EditToolbar = props => (
    <Toolbar {...props} >
        <SaveButton/>
    </Toolbar>
);

export const EmailEdit = props => {
    return (
        <Edit {...props} redirect="list">
            <SimpleForm toolbar={<EditToolbar/>}>
                <Grid container spacing={2}>
                    <Grid item xs={5}>
                        <TextInput fullWidth label="Name des Wettbewerbs" source="contestName"
                                   validate={validateContestName}/>
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput fullWidth label="Domain-Name" source="domainName"
                                   helperText="Geben Sie einen gültigen Domänennamen an, z. B.: abcd.com"
                                   validate={validateDomainName}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h5">
                            Genemigungs-Benachrichtigungen
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                     source="grantorApprovalTemplateId"
                                     helperText="E-Mail-Vorlage Id für die Bestätigung weitergeleitet an: hilfe@spendenwettbewerb.de"
                                     validate={validateGrantorApprovalTemplateId}/>
                    </Grid>
                    <Grid item xs={4}>
                        <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                     source="granteeApprovalTemplateId"
                                     helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über die Genehmigung verwendet wird"
                                     validate={validateGranteeApprovalTemplateId}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextInput fullWidth multiline label="Optional E-Mail Alternativtext"
                                   source="alternativeDateToAction"
                                   helperText="Alternativer Text als Ersatz für den Hinweis auf den Beginn der Stimmabgabe. z.B. Sie können sofort Stimmen sammeln"
                                   validate={validateAlternativeText}/>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h5">
                            Schwellenwertübertregung-Benachrichtigungen
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <BooleanInput label="Schwellenwert-Benachrichtigungen aktivieren"
                                      source="sendThresholdCrossEmailNotification"
                                      helperText="Ermöglicht das automatische Benachrichtigen der Projekte über übertretene Schwellenwerte. Alle Vorlagen-IDs müssen gesetzt sein, damit die Funktion funktioniert."
                                      defaultValue={false}/>
                    </Grid>
                    <FormDataConsumer>
                        {({formData: {sendThresholdCrossEmailNotification}, ...rest}) => <>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    Zwischenziel-Benachrichtigungen
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                             source="grantorThresholdCrossTemplateId"
                                             helperText="E-Mail-Vorlage Id für die Bestätigung des Übertretens eines Schwellenwertes. weitergeleitet an: hilfe@spendenwettbewerb.de"
                                             disabled={!sendThresholdCrossEmailNotification}
                                             validate={validateGrantorThresholdCrossTemplateId}/>
                            </Grid>
                            <Grid item xs={4}>
                                <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                             source="granteeThresholdCrossTemplateId"
                                             helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über das Übertreten eines Schwellenwertes verwendet wird"
                                             disabled={!sendThresholdCrossEmailNotification}
                                             validate={validateGranteeThresholdCrossTemplateId}/>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    Letze Schwelle erreicht-Benachrichtigungen
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                             source="grantorFinalThresholdCrossTemplateId"
                                             helperText="E-Mail-Vorlage Id für die Bestätigung des Übertretens des finalen Schwellenwertes. weitergeleitet an: hilfe@spendenwettbewerb.de"
                                             disabled={!sendThresholdCrossEmailNotification}
                                             validate={validateGrantorThresholdCrossTemplateId}/>
                            </Grid>
                            <Grid item xs={4}>
                                <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                             source="granteeFinalThresholdCrossTemplateId"
                                             helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über das Übertreten des finalen Schwellenwertes verwendet wird"
                                             disabled={!sendThresholdCrossEmailNotification}
                                             validate={validateGranteeThresholdCrossTemplateId}/>
                            </Grid>
                        </>}
                    </FormDataConsumer>
                </Grid>
            </SimpleForm>
        </Edit>
    )
        ;
}

export const EmailCreate = props => (
    <Create {...props} redirect="list">
        <SimpleForm>
            <Grid container spacing={2}>
                <Grid item xs={5}>
                    <TextInput fullWidth label="Name des Wettbewerbs" source="contestName"
                               validate={validateContestName}/>
                </Grid>
                <Grid item xs={5}>
                    <TextInput fullWidth label="Domain-Name" source="domainName"
                               helperText="Geben Sie einen gültigen Domänennamen an, z. B.: abcd.com"
                               validate={validateDomainName}/>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h5">
                        Genemigungs-Benachrichtigungen
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                 source="grantorApprovalTemplateId"
                                 helperText="E-Mail-Vorlage Id für die Bestätigung weitergeleitet an: hilfe@spendenwettbewerb.de"
                                 validate={validateGrantorApprovalTemplateId}/>
                </Grid>
                <Grid item xs={4}>
                    <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                 source="granteeApprovalTemplateId"
                                 helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über die Genehmigung verwendet wird"
                                 validate={validateGranteeApprovalTemplateId}/>
                </Grid>
                <Grid item xs={12}>
                    <TextInput fullWidth multiline label="Optional E-Mail Alternativtext"
                               source="alternativeDateToAction"
                               helperText="Alternativer Text als Ersatz für den Hinweis auf den Beginn der Stimmabgabe. z.B. Sie können sofort Stimmen sammeln"
                               validate={validateAlternativeText}/>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h5">
                        Schwellenwertübertregung-Benachrichtigungen
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <BooleanInput label="Schwellenwert-Benachrichtigungen aktivieren"
                                  source="sendThresholdCrossEmailNotification"
                                  helperText="Ermöglicht das automatische Benachrichtigen der Projekte über übertretene Schwellenwerte. Alle Vorlagen-IDs müssen gesetzt sein, damit die Funktion funktioniert."
                                  defaultValue={false}/>
                </Grid>
                <FormDataConsumer>
                    {({formData: {sendThresholdCrossEmailNotification}, ...rest}) => <>
                        <Grid item xs={12}>
                            <Typography variant="h6">
                                Zwischenziel-Benachrichtigungen
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                         source="grantorThresholdCrossTemplateId"
                                         helperText="E-Mail-Vorlage Id für die Bestätigung des Übertretens eines Schwellenwertes. weitergeleitet an: hilfe@spendenwettbewerb.de"
                                         disabled={!sendThresholdCrossEmailNotification}
                                         validate={validateGrantorThresholdCrossTemplateId}/>
                        </Grid>
                        <Grid item xs={4}>
                            <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                         source="granteeThresholdCrossTemplateId"
                                         helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über das Übertreten eines Schwellenwertes verwendet wird"
                                         disabled={!sendThresholdCrossEmailNotification}
                                         validate={validateGranteeThresholdCrossTemplateId}/>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6">
                                Letze Schwelle erreicht-Benachrichtigungen
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <NumberInput label="E-Mail-Vorlagen-ID für die Benachrichtigung der Kampagnenmanager"
                                         source="grantorFinalThresholdCrossTemplateId"
                                         helperText="E-Mail-Vorlage Id für die Bestätigung des Übertretens des finalen Schwellenwertes. weitergeleitet an: hilfe@spendenwettbewerb.de"
                                         disabled={!sendThresholdCrossEmailNotification}
                                         validate={validateGrantorThresholdCrossTemplateId}/>
                        </Grid>
                        <Grid item xs={4}>
                            <NumberInput label="E-Mail-Vorlagen-ID für die Teilnehmer-Benachrichtigung"
                                         source="granteeFinalThresholdCrossTemplateId"
                                         helperText="Id der E-Mail-Vorlage, die für die Benachrichtigung der Teilnehmer über das Übertreten des finalen Schwellenwertes verwendet wird"
                                         disabled={!sendThresholdCrossEmailNotification}
                                         validate={validateGranteeThresholdCrossTemplateId}/>
                        </Grid>
                    </>}
                </FormDataConsumer>
            </Grid>
        </SimpleForm>
    </Create>
);
